import { z } from 'zod';

import type { NewQuestion, Question, QuestionEdit } from '@/api/questionSets';
import { CATEGORIES, DIFFICULTIES, type Category, type Difficulty } from '@/api/vocabularies';

/**
 * **The three fields a question is, as one schema** — §6.9's *Add question* and its *Edit* write the
 * same question, so they are described once (`.claude/rules/frontend.md` → Forms: the zod schema
 * **is** the form type), the way `vacancyFields.ts` describes a vacancy once for §6.8 and
 * `personFields.ts` a person once for §6.4 and §6.5.
 *
 * ⚠️ **`is_active` is not here, and its absence is the point.** `QuestionIn` does not carry it (*"a
 * new question is active; retirement is the archive action"*) and neither does `QuestionUpdate` —
 * archiving is an action (ADR 0005, the shape of ADR 0010). A form field for it would be the first
 * sign that archiving had become a field edit, so `questionWrites.test.tsx` asserts the schema's keys
 * **equal** `QuestionIn`'s own properties and that no body ever names it.
 *
 * ⚠️ **`position` is a field the model has again, and it is still not here** (2026-09-24, ADR 0019).
 * The two intervening statements are both retracted: *"order is a property of a question's place in a
 * set"* was true of `QuestionSetItem`, which is gone, and *"the model has no such field"* is false —
 * `Question.position` is back, and it is what `create_interview` freezes into `Score.position`. What is
 * unchanged is that **no form writes it**: a new question is appended last and choosing where it lands
 * is the reorder's job, because two routes writing one integer would be two definitions of the order a
 * freeze reads. `author` is the field a body must never carry, for a stronger reason than either: an
 * author a client could assert would be an author the write gate could not trust — and since ADR 0019
 * it is not even on the payload to echo back.
 *
 * ⚠️ **And `set` is not here either.** The set is the **address** of both routes
 * (`…/question-sets/{set_id}/questions/…`); a set id in the body would be a second place to say which
 * set is being written, one of them unguarded by the write gate.
 */

/**
 * ⚠️ **The two bounds `QuestionIn` declares, both of them the document's own numbers.**
 *
 * `QuestionIn.text` is `minLength: 1, maxLength: 2000` — the numbers are read out of
 * `openapi.snapshot.json` by `questionWrites.test.tsx`, exactly as `vacancyFields.ts`'s three bounds
 * are, so a bound the backend changes fails the suite — **with the snapshot refreshed**, which is the
 * only day an assertion over a committed document can fire — instead of failing in front of somebody
 * typing. ⚠️ **The qualifier is BUG-070's**, and without it this sentence contradicted the paragraph
 * directly below it.
 *
 * ⚠️ **The maximum arrived with BUG-058 (2026-08-09), and this module stated the opposite until
 * then** — *"there is no maximum, and none is invented here"*, argued from `Question.text` being a
 * `TextField`. The **column** still is one; what bounds the wording is the **schema**, and
 * `QuestionUpdate.text` carries the same pair, so the edit path is bounded too. The absence assertion
 * written to fail on exactly this day (*"the day the backend bounds the wording, the suite says derive
 * it"*) did **not** fail, because it read the committed snapshot rather than the live document — so it
 * is replaced by an **equality** in both directions: the number below is the document's, and the
 * number `QuestionDialog`'s field enforces is the document's.
 *
 * The value is written out rather than read from the document at runtime for the reason
 * `addendumFields.ts` records: TypeScript cannot read a `maxLength` out of a JSON Schema, and the
 * document is not a runtime import. The derivation is therefore at **test** level, which is the same
 * device `QUESTION_TEXT_MIN_LENGTH`, `VACANCY_LIMITS` and `ADDENDUM_MAX_TEXT` already stand on.
 */
export const QUESTION_TEXT_MIN_LENGTH = 1;
export const QUESTION_TEXT_MAX_LENGTH = 2000;

/**
 * The two vocabularies as `z.enum` needs them: a non-empty tuple.
 *
 * ⚠️ **Derived, never hand-written.** `CATEGORIES` and `DIFFICULTIES` come from
 * `components['schemas']['Category' | 'Difficulty']` through a `Record` over the type
 * (`api/vocabularies.ts`), so a value added on the backend reaches this form — and these `Select`s —
 * without an edit, and one removed stops `tsc` there rather than here. The assertion is only about
 * the *shape* zod asks for (at least one member); the members themselves are the document's, and
 * `questionWrites.test.tsx` asserts the rendered option set equals the document's enum.
 */
const CATEGORY_VALUES = CATEGORIES as [Category, ...Category[]];
const DIFFICULTY_VALUES = DIFFICULTIES as [Difficulty, ...Difficulty[]];

export const questionFormSchema = z.object({
  /**
   * Trimmed **by the schema**, so the wording sent and the wording the person can see they typed are
   * the same string — and a question of nothing but spaces is empty rather than three characters
   * long. It matters more here than anywhere else in the product: this string is copied into every
   * score of every interview arranged from now on (ADR 0004), where nobody can correct it.
   *
   * ⚠️ **No `.max()` beside the `.min()`, and the maximum is carried by the field instead**
   * (`QuestionDialog`'s `maxLength`, `AddendumDialog`'s arrangement). `text` has **one** message in
   * the catalogue — `questions:form.errors.text`, *"A question is required — write the wording an
   * interviewer will read out."* — which is the wrong sentence for a wording that is too long, and a
   * refusal that names the wrong problem is worse than the browser stopping the keystroke. A second
   * refusal needs a second string; adding one is a change to §6.9's copy, not to this schema.
   *
   * The one value that can still exceed the bound is a question written **before** BUG-058 bounded it
   * and then edited — a native `maxLength` truncates typing and paste, never a value the field is
   * given — and that lands on the dialog's own failure line.
   */
  text: z.string().trim().min(QUESTION_TEXT_MIN_LENGTH),
  /**
   * Both are **required with no default**, which is `QuestionIn`'s own shape: it declares no default
   * for either, so the API makes the caller decide and this form does the same. Defaulting them would
   * classify a question nobody classified, and category is not decoration — it is one of the two
   * filters this bank is read through (§6.9).
   */
  category: z.enum(CATEGORY_VALUES),
  difficulty: z.enum(DIFFICULTY_VALUES),
});

export type QuestionForm = z.infer<typeof questionFormSchema>;

/** The question as the form holds it — the defaults of an *Edit*. */
export const fieldsOf = (question: Question): QuestionForm => ({
  text: question.text,
  category: question.category,
  difficulty: question.difficulty,
});

/**
 * `QuestionIn`'s **three fields and nothing else** — no `tenant` and no `author` (the session
 * resolves both, `.claude/rules/tenancy.md`), and no `is_active` (archiving is an action). Written out
 * rather than spread from the form so a fourth key cannot arrive by accident, and asserted as an
 * **equality** against the recorded request body.
 */
export const creationOf = (submitted: QuestionForm): NewQuestion => ({
  text: submitted.text,
  category: submitted.category,
  difficulty: submitted.difficulty,
});

/**
 * **Only what changed, and never an explicit `null`.**
 *
 * `QuestionUpdate` is *"every field optional, only the ones sent are written"*, and optional there
 * means **omittable, not nullable**: `{"text": null}` is a **422**, deliberately, so a `NULL` is
 * never written onto a NOT NULL column. Every value here comes from the form — one trimmed string and
 * two members of a derived enum — so a `null` is not merely avoided but unproduceable.
 *
 * The three comparisons are written out rather than looped, as `changesOf` in `vacancyFields.ts` is
 * and for the same reason (the fields are not one type). The loop is replaced by a test: a submission
 * differing in **every** field must produce a body whose keys are exactly `questionFormSchema`'s own,
 * so a fourth field added above and forgotten here fails the suite rather than being undiffable.
 */
export function changesOf(question: Question, submitted: QuestionForm): QuestionEdit {
  const changes: QuestionEdit = {};

  if (submitted.text !== question.text) changes.text = submitted.text;
  if (submitted.category !== question.category) changes.category = submitted.category;
  if (submitted.difficulty !== question.difficulty) changes.difficulty = submitted.difficulty;

  return changes;
}
